import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useCreateInstance, useInstances } from '../hooks/useInstances'
import { useAuthStore } from '../store/authStore'
import { instancesApi, meApi, metaApi, templatesApi, usersApi } from '../api/client'
import type { PlatformImage } from '../api/client'
import { capture } from '../lib/analytics'
import { useAvailableImages } from '../hooks/useImages'
import { Badge, Button, Card, EmptyState, Field, Input, LinkButton, PageHeader, Textarea } from '../components/ui'

// Free-user 3-step wizard options. Sector values are stable keys translated in
// the UI; user-count is stored as a human-readable range string.
const SECTORS = ['retail', 'manufacturing', 'services', 'healthcare', 'education', 'technology', 'logistics', 'construction', 'food_beverage', 'other'] as const
const USER_COUNTS = ['1-5', '6-10', '11-25', '26-50', '50+'] as const

// The wizard's radio-card look. These are selections, not actions — a `Button`
// would repaint them as filled controls and lose the selected/unselected state,
// so they stay hand-rolled and share this one spelling instead.
const selectCard = (selected: boolean) =>
  `rounded-lg border-2 transition-all ${
    selected ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-gray-300'
  }`

/**
 * One button per series, not one per catalogue row.
 *
 * `/images/available` returns every enabled row, and a series legitimately has
 * more than one the first time an admin does the thing this feature exists for:
 * pinning `odoo:19.0-20260908` alongside the rolling `odoo:19`. The wizard picks
 * a *series* and the backend resolves that series' default at create time, so
 * two rows in one series rendered two identical "Odoo 19" buttons with duplicate
 * React keys, both resolving to the same image.
 *
 * The default row wins, so the `desc` line under the button names the image the
 * instance will actually get.
 */
export function oneRowPerSeries(rows: PlatformImage[] | undefined, kind: PlatformImage['kind']) {
  const bySeries = new Map<string, PlatformImage>()
  for (const row of rows ?? []) {
    if (row.kind !== kind) continue
    const seen = bySeries.get(row.series)
    if (!seen || (row.is_default && !seen.is_default)) bySeries.set(row.series, row)
  }
  return [...bySeries.values()]
}

/** The filled dot on a radio-card. */
function RadioDot({ selected }: { selected: boolean }) {
  return (
    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selected ? 'border-brand-500 bg-brand-500' : 'border-gray-300'}`} />
  )
}

function LimitReachedScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: meta } = useQuery({ queryKey: ['meta'], queryFn: metaApi.get, staleTime: Infinity })

  return (
    <div className="p-4 sm:p-8 max-w-lg mx-auto">
      <Card padded={false} className="p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('create.limitReached.title')}</h2>
        <p className="text-sm text-gray-500 mb-6">{t('create.limitReached.body')}</p>

        <div className="flex flex-col gap-3">
          {meta?.support_email && (
            // A mailto link, not an action: `Button` renders a <button> and
            // would drop the navigation. Left as a styled anchor.
            <a
              href={`mailto:${meta.support_email}?subject=${encodeURIComponent(t('create.limitReached.emailSubject'))}`}
              className="w-full py-2.5 px-4 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors text-center"
            >
              {t('create.limitReached.contactSales')}
            </a>
          )}
          {/* TODO: replace with self-checkout link when available */}
          <Button variant="secondary" fullWidth onClick={() => navigate('/dashboard')}>
            {t('create.limitReached.back')}
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default function CreateInstancePage() {
  const navigate = useNavigate()
  const createMutation = useCreateInstance()
  const { t } = useTranslation()
  const role = useAuthStore((s) => s.role)
  const isAdmin = role === 'admin'

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: meApi.get,
    enabled: role !== 'admin',
  })

  // Free users go through 3 extra leading steps (sector, user count, reason)
  // and their instance is created in pending_approval for admin review.
  const isFreeUser = !isAdmin && me?.plan === 'free'
  const preCount = isFreeUser ? 3 : 0
  const TOTAL_STEPS = (isAdmin ? 5 : 4) + preCount
  const { data: instances } = useInstances()
  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: templatesApi.list,
    staleTime: 60_000,
  })
  const { data: allUsers = [] } = useQuery({
    queryKey: ['users', 'list-all'],
    queryFn: usersApi.listAll,
    enabled: isAdmin,
    staleTime: 60_000,
  })
  const available = useAvailableImages()
  const odooVersions = oneRowPerSeries(available.data, 'odoo')
    .map((i) => ({ value: i.series, labelKey: `Odoo ${i.series}`, desc: i.rolling ? t('images.rolling') : i.ref }))
  const pgVersions = oneRowPerSeries(available.data, 'postgres')
    .map((i) => ({ value: i.series, label: `PostgreSQL ${i.series}`, desc: i.rolling ? t('images.rolling') : i.ref }))

  const [step, setStep] = useState(1)
  const [preStep, setPreStep] = useState(1) // free-user lead-in: 1=sector 2=users 3=reason, >3 = into main wizard
  const [sector, setSector] = useState('')
  const [userCount, setUserCount] = useState('')
  const [requestReason, setRequestReason] = useState('')
  const [name, setName] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [version, setVersion] = useState('')
  const [pgVersion, setPgVersion] = useState('')
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null)
  const [defaultAutoSelected, setDefaultAutoSelected] = useState(false)
  const [assignedTo, setAssignedTo] = useState<string>('')
  const [error, setError] = useState('')
  const [step1Validating, setStep1Validating] = useState(false)

  // Free users who already have an instance: redirect to that instance's detail page
  const shouldRedirectToInstance = role !== 'admin' && me && instances !== undefined && instances.length >= me.instance_limit && instances.length === 1
  const redirectInstanceId = shouldRedirectToInstance ? instances[0].id : null

  useEffect(() => {
    if (redirectInstanceId) {
      navigate(`/instances/${redirectInstanceId}`, { replace: true })
    }
  }, [redirectInstanceId, navigate])

  // A template built for another Odoo series carries that series' code; the
  // API refuses it, so the wizard does not offer it. No series = fits any.
  const fittingTemplates = useMemo(
    () => templates?.filter((tmpl) => !tmpl.odoo_version || tmpl.odoo_version === version),
    [templates, version],
  )

  // Auto-select the default template on first load if one exists and user hasn't chosen.
  useEffect(() => {
    if (defaultAutoSelected || templateId || !version) return
    const def = fittingTemplates?.find((t) => t.is_default && t.is_active)
    if (def) {
      setTemplateId(def.id)
      setDefaultAutoSelected(true)
    }
  }, [fittingTemplates, defaultAutoSelected, templateId, version])

  // Changing the version can leave the chosen template behind.
  useEffect(() => {
    if (templateId && fittingTemplates && !fittingTemplates.some((t) => t.id === templateId)) {
      setTemplateId(null)
    }
  }, [fittingTemplates, templateId])

  // Seed the version pickers from the catalogue's defaults rather than a
  // hardcoded literal, so the wizard never preselects a series the catalogue
  // no longer offers. An admin can disable the default row for a kind
  // without disabling every row in it, so a kind can have entries but no
  // `is_default` — fall back to the first enabled row for that kind rather
  // than leaving the selection empty (the step 2 "Next" button below stays
  // disabled until both pickers are non-empty, so this only shortens that
  // window, it isn't the only guard against it).
  useEffect(() => {
    const odoo = (available.data ?? []).filter((i) => i.kind === 'odoo')
    const d = odoo.find((i) => i.is_default) ?? odoo[0]
    if (d) setVersion((v) => v || d.series)
    const pg = (available.data ?? []).filter((i) => i.kind === 'postgres')
    const p = pg.find((i) => i.is_default) ?? pg[0]
    if (p) setPgVersion((v) => v || p.series)
  }, [available.data])

  if (role !== 'admin' && me && instances !== undefined) {
    const count = instances.length
    if (count >= me.instance_limit) {
      if (count === 1) {
        return null // redirecting via useEffect
      }
      return <LimitReachedScreen />
    }
  }

  const subdomainFromName = (n: string) =>
    n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  const handleNameChange = (v: string) => {
    setName(v)
    if (!subdomain || subdomain === subdomainFromName(name)) {
      setSubdomain(subdomainFromName(v))
    }
  }

  const handleStep1Next = async () => {
    setError('')
    setStep1Validating(true)
    try {
      await instancesApi.validate({ name, subdomain })
      setError('')
      setStep(2)
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'create.submitError'
      setError(msg)
    } finally {
      setStep1Validating(false)
    }
  }

  const handleSubmit = async () => {
    setError('')
    try {
      const result = await createMutation.mutateAsync({
        name,
        subdomain,
        odoo_version: version,
        pg_version: pgVersion,
        ...(templateId ? { template_id: templateId } : {}),
        ...(isAdmin && assignedTo ? { assigned_to: assignedTo } : {}),
        ...(isFreeUser && sector ? { sector } : {}),
        ...(isFreeUser && userCount ? { user_count: userCount } : {}),
        ...(isFreeUser && requestReason ? { request_reason: requestReason } : {}),
      })
      capture('instance_created', {
        instance_id: result.id,
        odoo_version: version,
        from_template: !!templateId,
      })
      // Free-user instances start pending_approval — send them to the dashboard
      // where the pending badge explains the wait.
      navigate(isFreeUser ? '/dashboard' : `/instances/${result.id}`)
    } catch (e: unknown) {
      const raw = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'create.submitError'
      setError(raw === 'instance_limit_reached' ? t('create.limitReached.title') : t(raw, raw))
    }
  }

  const selectedTemplate = templates?.find((t) => t.id === templateId) ?? null
  const assignedToUser = allUsers.find((u) => u.id === assignedTo) ?? null
  const nonAdminUsers = allUsers.filter((u) => u.role !== 'admin')

  // Free-user lead-in phase gating.
  const inPre = isFreeUser && preStep <= 3
  const showWizard = !isFreeUser || preStep > 3
  // Non-admins skip step 4 (assign-to) and land on 5, which used to read
  // "Step 5 of 4" on the review screen and fill a 4-segment bar with 5.
  const mainStep = !isAdmin && step === 5 ? 4 : step
  const displayStep = inPre ? preStep : preCount + mainStep

  return (
    <div className="p-4 sm:p-8 max-w-2xl mx-auto">
      <PageHeader
        title={t('create.title')}
        description={t('create.step', { current: displayStep, total: TOTAL_STEPS })}
      />

      <div className="flex gap-1 mb-8">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
          <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= displayStep ? 'bg-brand-500' : 'bg-gray-200'}`} />
        ))}
      </div>

      {/* Free-user lead-in: sector */}
      {inPre && preStep === 1 && (
        <Card className="space-y-5">
          <div>
            <h2 className="font-semibold text-gray-800">{t('create.free.sector.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('create.free.sector.subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {SECTORS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSector(s)}
                className={`${selectCard(sector === s)} p-3 text-left text-sm font-medium ${sector === s ? 'text-gray-900' : 'text-gray-700'}`}
              >
                {t(`create.free.sectors.${s}`)}
              </button>
            ))}
          </div>
          <Button fullWidth onClick={() => setPreStep(2)} disabled={!sector}>
            {t('create.free.next')}
          </Button>
        </Card>
      )}

      {/* Free-user lead-in: user count */}
      {inPre && preStep === 2 && (
        <Card className="space-y-5">
          <div>
            <h2 className="font-semibold text-gray-800">{t('create.free.users.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('create.free.users.subtitle')}</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {USER_COUNTS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setUserCount(c)}
                className={`${selectCard(userCount === c)} p-3 text-center text-sm font-medium ${userCount === c ? 'text-gray-900' : 'text-gray-700'}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
            {t('create.free.users.freeNote')}
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setPreStep(1)}>
              {t('create.free.back')}
            </Button>
            <Button className="flex-1" onClick={() => setPreStep(3)} disabled={!userCount}>
              {t('create.free.next')}
            </Button>
          </div>
        </Card>
      )}

      {/* Free-user lead-in: optional reason */}
      {inPre && preStep === 3 && (
        <Card className="space-y-5">
          <div>
            <h2 className="font-semibold text-gray-800">{t('create.free.reason.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('create.free.reason.subtitle')}</p>
          </div>
          <Textarea
            value={requestReason}
            onChange={(e) => setRequestReason(e.target.value)}
            rows={4}
            placeholder={t('create.free.reason.placeholder')}
            className="resize-none"
          />
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setPreStep(2)}>
              {t('create.free.back')}
            </Button>
            <Button className="flex-1" onClick={() => setPreStep(4)}>
              {t('create.free.next')}
            </Button>
          </div>
        </Card>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {showWizard && step === 1 && (
        <Card className="space-y-5">
          <h2 className="font-semibold text-gray-800">{t('create.step1.title')}</h2>

          <Field label={t('create.step1.name')}>
            {(p) => (
              <Input
                {...p}
                type="text"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder={t('create.step1.namePlaceholder')}
              />
            )}
          </Field>

          <Field label={t('create.step1.subdomain')} hint={t('create.step1.subdomainHint')}>
            {(p) => (
              // A joined input + suffix: `Input` carries its own border and
              // radius, which would break the seam with ".provisyer.com".
              <div className="flex rounded-lg overflow-hidden border border-gray-300 focus-within:ring-2 focus-within:ring-brand-500">
                <input
                  {...p}
                  type="text"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="acme"
                  className="flex-1 px-3 py-2 text-sm focus:outline-none"
                />
                <span className="bg-gray-50 px-3 py-2 text-sm text-gray-500 border-l border-gray-300">
                  .provisyer.com
                </span>
              </div>
            )}
          </Field>

          <Button
            fullWidth
            onClick={handleStep1Next}
            disabled={!name || !subdomain}
            loading={step1Validating}
          >
            {step1Validating ? t('create.step3.submitting') : t('create.step1.next')}
          </Button>
        </Card>
      )}

      {showWizard && step === 2 && (
        <Card className="space-y-5">
          <h2 className="font-semibold text-gray-800">{t('create.step2.title')}</h2>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('create.step2.odooLabel')}</p>
            <div className="grid grid-cols-2 gap-3">
              {odooVersions.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => setVersion(v.value)}
                  className={`${selectCard(version === v.value)} p-4 text-left`}
                >
                  <div className="font-semibold text-gray-900">{v.labelKey}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{v.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('create.step2.dbLabel')}</p>
            <div className="grid grid-cols-3 gap-3">
              {pgVersions.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => setPgVersion(v.value)}
                  className={`${selectCard(pgVersion === v.value)} p-3 text-left`}
                >
                  <div className="font-semibold text-gray-900 text-sm">{v.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{v.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setStep(1)}>
              {t('create.step2.back')}
            </Button>
            <Button className="flex-1" onClick={() => setStep(3)} disabled={!version || !pgVersion}>
              {t('create.step2.next')}
            </Button>
          </div>
        </Card>
      )}

      {showWizard && step === 3 && (
        <Card className="space-y-5">
          <div>
            <h2 className="font-semibold text-gray-800">{t('create.template.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('create.template.subtitle')}</p>
          </div>

          <div className="space-y-2">
            {/* None option */}
            <button
              type="button"
              onClick={() => setTemplateId(null)}
              className={`${selectCard(templateId === null)} w-full p-4 text-left`}
            >
              <div className="flex items-center gap-3">
                <RadioDot selected={templateId === null} />
                <div>
                  <div className="font-medium text-gray-900 text-sm">{t('create.template.none')}</div>
                  <div className="text-xs text-gray-500">{t('create.template.noneDesc')}</div>
                </div>
              </div>
            </button>

            {/* Template options */}
            {fittingTemplates && fittingTemplates.length > 0 ? fittingTemplates.map((tmpl) => {
              const isSelected = templateId === tmpl.id
              const isExpanded = expandedTemplateId === tmpl.id
              const moduleCount = tmpl.modules?.length ?? 0
              return (
                <div key={tmpl.id} className={`${selectCard(isSelected)} w-full`}>
                  <button
                    type="button"
                    onClick={() => setTemplateId(tmpl.id)}
                    className="w-full p-4 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <RadioDot selected={isSelected} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium text-gray-900 text-sm">{tmpl.name}</div>
                          {tmpl.is_default && <Badge tone="accent">{t('create.template.default')}</Badge>}
                        </div>
                        {tmpl.description && <div className="text-xs text-gray-500 mt-0.5">{tmpl.description}</div>}
                        {moduleCount > 0 && (
                          <div className="text-xs text-brand-600 mt-1">
                            {t('create.template.moduleCount', { count: moduleCount })}
                          </div>
                        )}
                        {isSelected && moduleCount >= 5 && (
                          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 inline-block">
                            {t('create.template.timeWarning', { minutes: moduleCount * 3 })}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                  {moduleCount > 0 && (
                    <div className="px-4 pb-3">
                      <LinkButton
                        onClick={(e) => {
                          e.stopPropagation()
                          setExpandedTemplateId(isExpanded ? null : tmpl.id)
                        }}
                      >
                        {isExpanded ? t('create.template.hideModules') : t('create.template.showModules')}
                      </LinkButton>
                      {isExpanded && (
                        <ul className="mt-2 space-y-1 border-t border-gray-200 pt-2">
                          {tmpl.modules!.map((mod) => (
                            <li key={mod.id} className="text-xs text-gray-600">
                              <span className="font-medium text-gray-800">{mod.module_name}</span>
                              {mod.description && (
                                <span className="text-gray-500"> - {mod.description}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )
            }) : (
              <EmptyState title={t('create.template.empty')} />
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setStep(2)}>
              {t('create.template.back')}
            </Button>
            <Button className="flex-1" onClick={() => setStep(isAdmin ? 4 : 5)}>
              {t('create.template.next')}
            </Button>
          </div>
        </Card>
      )}

      {/* Admin-only step: Assign To */}
      {showWizard && step === 4 && isAdmin && (
        <Card className="space-y-5">
          <div>
            <h2 className="font-semibold text-gray-800">{t('create.step4.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('create.step4.subtitle')}</p>
          </div>

          <div className="space-y-2">
            {/* Self (admin) option */}
            <button
              type="button"
              onClick={() => setAssignedTo('')}
              className={`${selectCard(assignedTo === '')} w-full p-4 text-left`}
            >
              <div className="flex items-center gap-3">
                <RadioDot selected={assignedTo === ''} />
                <div className="font-medium text-gray-900 text-sm">{t('create.step4.me')}</div>
              </div>
            </button>

            {/* Non-admin users */}
            {nonAdminUsers.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => setAssignedTo(u.id)}
                className={`${selectCard(assignedTo === u.id)} w-full p-4 text-left`}
              >
                <div className="flex items-center gap-3">
                  <RadioDot selected={assignedTo === u.id} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm">{u.email}</div>
                    {u.plan && <div className="text-xs text-gray-500 mt-0.5">{u.plan}</div>}
                  </div>
                </div>
              </button>
            ))}

            {nonAdminUsers.length === 0 && <EmptyState title={t('create.step4.noUsers')} />}
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setStep(3)}>
              {t('create.step4.back')}
            </Button>
            <Button className="flex-1" onClick={() => setStep(5)}>
              {t('create.step4.next')}
            </Button>
          </div>
        </Card>
      )}

      {showWizard && step === 5 && (
        <Card className="space-y-5">
          <h2 className="font-semibold text-gray-800">{t('create.step3.title')}</h2>

          <div className="rounded-lg bg-gray-50 divide-y divide-gray-200">
            {[
              { label: t('create.step3.name'),     value: name },
              { label: t('create.step3.url'),      value: `https://${subdomain}.provisyer.com` },
              { label: t('create.step3.version'),  value: `v${version}` },
              { label: t('create.step3.dbVersion'),         value: `PostgreSQL ${pgVersion}` },
              { label: t('create.step3.database'), value: subdomain },
              { label: t('create.step3.template'), value: selectedTemplate ? selectedTemplate.name : t('create.step3.templateNone') },
              ...(isAdmin ? [{ label: t('create.step4.title'), value: assignedToUser ? assignedToUser.email : t('create.step4.me') }] : []),
              ...(isFreeUser && sector ? [{ label: t('create.free.sector.label'), value: t(`create.free.sectors.${sector}`) }] : []),
              ...(isFreeUser && userCount ? [{ label: t('create.free.users.label'), value: userCount }] : []),
            ].map((row) => (
              <div key={row.label} className="flex justify-between px-4 py-3 text-sm">
                <span className="text-gray-500">{row.label}</span>
                <span className="font-medium text-gray-900">{row.value}</span>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500">{t(isFreeUser ? 'create.free.reviewHint' : 'create.step3.hint')}</p>

          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setStep(isAdmin ? 4 : 3)}>
              {t('create.step3.back')}
            </Button>
            <Button
              className="flex-1"
              onClick={handleSubmit}
              loading={createMutation.isPending}
              disabled={!version || !pgVersion}
            >
              {createMutation.isPending ? t('create.step3.submitting') : t(isFreeUser ? 'create.free.submit' : 'create.step3.submit')}
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}
